import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { RecommendedActionTypeEnum } from "@app/projects/enumerators";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { QESCheckbox } from "@app/shared/qes-standards";
import classNames from "classnames";
import { uniqueId } from "lodash";
import { FC, useContext } from "react";

interface AccuracyRecommendedActionFilterProps {
  onRecommendedActionChange: (recommendedAction: number) => void;
  onRecommendedActionChangeDropdown: (recommendedActions: number[]) => void;
  recommendedActionIds: number[];
  recommendedActionReasonsFilterDisabled: boolean;
  onShowAllReasonsChange: () => void;
  showAllRecommendedActionReasons: boolean;
}

const AccuracyRecommendedActionFilter: FC<AccuracyRecommendedActionFilterProps> = (props: AccuracyRecommendedActionFilterProps) => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const renderAllActionableReasonsCheckbox = () => {
    return (<>
      <label className="col-12 col-form-label ptb0 text-left">Please select the actions to include:</label>
      <div className={classNames("col-xl-10 col-lg-9 mt0")}>
        <QESCheckbox
          key={"allActionsCheckbox"}
          id={uniqueId("allActionsCheckbox")}
          label={"Add all lower-ranking reasons to report"}
          containerClassName="form-check"
          className="form-check-input mt-0"
          checked={props.showAllRecommendedActionReasons}
          onChange={() => props.onShowAllReasonsChange()}
          testId={`accuracy-show-all-recommended-actions-checkbox`}
          disabled={props.recommendedActionReasonsFilterDisabled}
        />
      </div>
    </>);
  }

  const renderRecommendedActions = () => {
    // Prioritization derrived from ToRecommendedActionPriority of RecommendedActionTypeExtensions in QESI-Accuracy.Contract solution
    const prioritizedRecommendedActions: RecommendedActionTypeEnum[] = [RecommendedActionTypeEnum.Suppress, RecommendedActionTypeEnum.Investigate,
    RecommendedActionTypeEnum.Update, RecommendedActionTypeEnum.Supplement,
    RecommendedActionTypeEnum.Enrich, RecommendedActionTypeEnum.Standardize,
    RecommendedActionTypeEnum.Retain
    ];
    const dropdownItems: DropdownItem<number>[] = [];
    const selectedItems: DropdownItem<number>[] = [];
    if (featureFlags.has(Features.ENABLE_VIEW_BY_INSPECTION_UPDATES)) {
      prioritizedRecommendedActions.forEach((v) => {
        const enumValue = Object.values(RecommendedActionTypeEnum).indexOf(v);
        if (v !== RecommendedActionTypeEnum.Unknown) {
          const item = {
            key: enumValue,
            name: RecommendedActionTypeEnum[v]
          };
          dropdownItems.push(item);

          if (props.recommendedActionIds.includes(enumValue)) {
            selectedItems.push(item);
          }
        }
      })
    }

    return (
      <div className="form-group row">
        {renderAllActionableReasonsCheckbox()}
        <label className="col-12 col-form-label ptb0 text-left">Select (based on hierarchy):</label>
        <div className={classNames("col-xl-10 col-lg-9 mt0")}>
          {featureFlags.has(Features.ENABLE_VIEW_BY_INSPECTION_UPDATES) ? (
            <MultiselectDropdown
              label="Recommended Action Filter"
              hideLabel={true}
              containerClassName="w-100"
              customSelectInputText={`${selectedItems.length} of ${dropdownItems.length} options selected`}
              items={dropdownItems}
              selectedItems={dropdownItems.filter((i) => props.recommendedActionIds.includes(i.key))}
              testId="accuracy-recommended-action-filter__dropdown"
              onSelectionChanged={(selected) => props.onRecommendedActionChangeDropdown(selected.map((i) => i.key))}
            />
          ) : (
            prioritizedRecommendedActions.map((v, i) => {
              const inputId = uniqueId(RecommendedActionTypeEnum[v]);
              const enumValue = Object.values(RecommendedActionTypeEnum).indexOf(v);

              return (v !== RecommendedActionTypeEnum.Unknown) ? (
                <QESCheckbox
                  key={v}
                  id={inputId}
                  label={RecommendedActionTypeEnum[v]}
                  containerClassName="form-check"
                  className="form-check-input mt-0"
                  value={enumValue.toString()}
                  checked={props.recommendedActionIds.indexOf(enumValue) > -1}
                  onChange={(e) => props.onRecommendedActionChange(parseInt(e.target.value, null))}
                  testId={`accuracy-recommended-action-checkbox--${i}`}
                />
              ) : null;
            })

          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="lead mb0 font-weight-bold">Recommended Actions Filter</p>
      {renderRecommendedActions()}
    </>
  );
}

export default AccuracyRecommendedActionFilter;
